/**
 * Navigation - Clean, Minimal, Professional
 *
 * Layout:
 * - LEFT: Clickable logo
 * - CENTER: Navigation links (desktop only)
 * - RIGHT: Auth state + CTA button + mobile menu toggle
 *
 * Styling: Taller, responsive, mobile-friendly
 */

import { useState } from "react";

/**
 * TODO (v3.1): Re-enable auth-aware nav once Supabase auth is fully wired.
 * The props below are already threaded from App.tsx → MainLayout → here.
 * When re-enabling, restore: useEffect/useRef imports, isUserMenuOpen state,
 * click-outside handler, displayName/userInitial, desktop dropdown, and
 * mobile auth section. See git history for the full implementation.
 */
interface NavigationNewProps {
  isAuthenticated?: boolean;
  userName?: string | null;
  userEmail?: string | null;
  onLogout?: () => void;
}

const NavigationNew = ({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  isAuthenticated: _isAuthenticated = false,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  userName: _userName,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  userEmail: _userEmail,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onLogout: _onLogout,
}: NavigationNewProps) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  return (
    <nav className="sticky top-0 left-0 right-0 z-50 bg-white shadow-sm">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-8 py-4 sm:py-8">
        <div className="flex items-center justify-between">
          {/* LEFT: Logo (Clickable) */}
          <a href="/" className="flex items-center flex-shrink-0">
            <img
              src="/aig_consulting_logos_v4_darkLogoLong.png"
              alt="AI Genius Automations"
              loading="eager"
              className="h-12 sm:h-14 w-auto object-contain"
            />
          </a>

          {/* CENTER: Navigation Links (Desktop) */}
          <div className="hidden lg:flex items-center gap-8">
            <a
              href="/#services"
              className="text-slate-700 hover:text-[#B87333] text-base font-semibold transition-all duration-300 hover:scale-105 relative group"
            >
              Services
              <span className="absolute left-0 right-0 bottom-0 h-0.5 bg-[#B87333] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
            </a>
            <a
              href="/case-studies"
              className="text-slate-700 hover:text-[#B87333] text-base font-semibold transition-all duration-300 hover:scale-105 relative group"
            >
              Case Studies
              <span className="absolute left-0 right-0 bottom-0 h-0.5 bg-[#B87333] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
            </a>
            <a
              href="/tools"
              className="text-slate-700 hover:text-[#B87333] text-base font-semibold transition-all duration-300 hover:scale-105 relative group"
            >
              Tools
              <span className="absolute left-0 right-0 bottom-0 h-0.5 bg-[#B87333] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
            </a>
            <a
              href="/courses"
              className="text-slate-700 hover:text-[#B87333] text-base font-semibold transition-all duration-300 hover:scale-105 relative group"
            >
              Courses
              <span className="absolute left-0 right-0 bottom-0 h-0.5 bg-[#B87333] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
            </a>
            <a
              href="/calendar"
              className="text-slate-700 hover:text-[#B87333] text-base font-semibold transition-all duration-300 hover:scale-105 relative group"
            >
              Events
              <span className="absolute left-0 right-0 bottom-0 h-0.5 bg-[#B87333] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
            </a>
            <a
              href="/about"
              className="text-slate-700 hover:text-[#B87333] text-base font-semibold transition-all duration-300 hover:scale-105 relative group"
            >
              About
              <span className="absolute left-0 right-0 bottom-0 h-0.5 bg-[#B87333] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
            </a>
            <a
              href="/contact"
              className="text-slate-700 hover:text-[#B87333] text-base font-semibold transition-all duration-300 hover:scale-105 relative group"
            >
              Contact
              <span className="absolute left-0 right-0 bottom-0 h-0.5 bg-[#B87333] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
            </a>
            {import.meta.env.VITE_DEV_MODE === "true" && (
              <a
                href="/system-settings"
                className="text-yellow-600 hover:text-yellow-700 text-base font-bold transition-all duration-300 hover:scale-105 relative group"
              >
                Dev Tools
                <span className="absolute left-0 right-0 bottom-0 h-0.5 bg-yellow-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
              </a>
            )}
          </div>

          {/* RIGHT: CTA Button + Mobile Menu Toggle */}
          <div className="flex items-center gap-4">
            {/*
             * TODO (v3.1): Auth-aware nav — Sign In link + user dropdown.
             * The props (isAuthenticated, userName, userEmail, onLogout) are
             * already wired through App.tsx → MainLayout → here.
             * Re-enable once Supabase auth flow is fully tested.
             */}

            {/* CTA Button */}
            <a
              href="https://calendar.notion.so/meet/aigeniusconsulting/793ez04oz7"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:inline-flex items-center justify-center px-6 py-3 bg-slate-900 text-white rounded-lg font-semibold text-base hover:bg-[#B87333] transition-all duration-300 shadow-md hover:shadow-xl hover:scale-105"
            >
              Book a Strategy Call
            </a>

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 text-slate-700 hover:text-slate-900"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? (
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              ) : (
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden mt-4 pt-4 border-t border-slate-200">
            <div className="flex flex-col space-y-3">
              <a
                href="/#services"
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-slate-700 hover:text-[#B87333] text-lg font-semibold py-2 transition-all duration-300 hover:translate-x-2"
              >
                Services
              </a>
              <a
                href="/case-studies"
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-slate-700 hover:text-[#B87333] text-lg font-semibold py-2 transition-all duration-300 hover:translate-x-2"
              >
                Case Studies
              </a>
              <a
                href="/tools"
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-slate-700 hover:text-[#B87333] text-lg font-semibold py-2 transition-all duration-300 hover:translate-x-2"
              >
                Tools
              </a>
              <a
                href="/courses"
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-slate-700 hover:text-[#B87333] text-lg font-semibold py-2 transition-all duration-300 hover:translate-x-2"
              >
                Courses
              </a>
              <a
                href="/calendar"
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-slate-700 hover:text-[#B87333] text-lg font-semibold py-2 transition-all duration-300 hover:translate-x-2"
              >
                Events
              </a>
              <a
                href="/about"
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-slate-700 hover:text-[#B87333] text-lg font-semibold py-2 transition-all duration-300 hover:translate-x-2"
              >
                About
              </a>
              <a
                href="/contact"
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-slate-700 hover:text-[#B87333] text-lg font-semibold py-2 transition-all duration-300 hover:translate-x-2"
              >
                Contact
              </a>
              {import.meta.env.VITE_DEV_MODE === "true" && (
                <a
                  href="/system-settings"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-yellow-600 hover:text-yellow-700 text-lg font-bold py-2 transition-all duration-300 hover:translate-x-2"
                >
                  Dev Tools
                </a>
              )}

              {/* TODO (v3.1): Mobile auth section — Sign In link / user name + Sign Out */}

              <a
                href="https://calendar.notion.so/meet/aigeniusconsulting/793ez04oz7"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setIsMobileMenuOpen(false)}
                className="inline-flex items-center justify-center px-6 py-3 bg-slate-900 text-white rounded-lg font-semibold text-base hover:bg-[#B87333] transition-all duration-300 shadow-md hover:shadow-xl mt-2"
              >
                Book a Strategy Call
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default NavigationNew;
